import { useMemo, useState } from 'react';
import { useReportStore } from '../../../stores/useReportStore';
import Gauge from '../charts/Gauge';
import Bars from '../charts/Bars';
import SmoothLine from '../charts/SmoothLine';
import Section from '../widgets/Section';
import StatusPill from '../widgets/StatusPill';
import BasisBadge from '../widgets/BasisBadge';
import KPICard from '../widgets/KPICard';
import MiniStrip, { type MiniCell } from '../widgets/MiniStrip';
import ChannelBenchmarksTable from '../widgets/ChannelBenchmarksTable';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { AlertCircle, AlertTriangle, CheckCircle2, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatMoney } from '../../../utils/formatMoney';
import { realValue } from '../../../utils/realValue';
import { labelsFor } from '../../../data/reportLabels';
import type { AuditVertical } from '../../../types/audit';

const FINDING_ICONS: Record<string, { icon: typeof AlertCircle; color: string }> = {
  critical: { icon: AlertCircle, color: 'text-destructive' },
  warning: { icon: AlertTriangle, color: 'text-warning' },
  positive: { icon: CheckCircle2, color: 'text-success' },
  info: { icon: Info, color: 'text-info' },
};

const INITIAL_VISIBLE = 5;

function KeyInsightsList({ findings }: { findings: any[] }) {
  const [expanded, setExpanded] = useState(false);
  const hasMore = findings.length > INITIAL_VISIBLE;
  const visible = expanded ? findings : findings.slice(0, INITIAL_VISIBLE);

  return (
    <div>
      <h3 className="mb-3.5 text-base font-bold tracking-[-0.01em] text-foreground">Key Insights</h3>
      <div className="flex flex-col gap-2">
        {visible.map((f: any, i: number) => {
          const sev = f.severity || f.type || 'info';
          const { icon: Icon, color } = FINDING_ICONS[sev] || FINDING_ICONS.info;
          return (
            <div key={i} className="flex items-start gap-2.5 text-sm leading-normal">
              <Icon className={cn('mt-0.5 size-[17px] shrink-0', color)} />
              <span className="text-foreground/90">
                {String(f.text || f.finding || f.message || '')}
                <BasisBadge basis={f.basis} className="ml-1.5" />
              </span>
            </div>
          );
        })}
      </div>
      {hasMore && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-3 flex cursor-pointer items-center gap-1.5 text-sm font-medium text-foreground/70 transition-colors hover:text-foreground"
        >
          {expanded ? (
            <>Show less <ChevronUp className="size-3.5" /></>
          ) : (
            <>Show {findings.length - INITIAL_VISIBLE} more <ChevronDown className="size-3.5" /></>
          )}
        </button>
      )}
    </div>
  );
}

function fmt(n: number | undefined, currency?: string | null): string {
  if (n == null) return '--';
  return formatMoney(n, currency);
}

function fmtNum(n: number | undefined): string {
  if (n == null) return '--';
  return n.toLocaleString();
}

function fmtPct(n: number | undefined): string {
  if (n == null) return '--';
  return `${n.toFixed(1)}%`;
}

function getHealthScore(data: Record<string, unknown>): { overall: number; dimensions: Record<string, { score: number; detail: string }> } {
  const hs = data.healthscore as Record<string, unknown> | undefined;
  if (hs && typeof hs.overall === 'number') {
    return {
      overall: hs.overall as number,
      dimensions: {
        Growth: { score: (hs.growth as number) || 0, detail: '' },
        Efficiency: { score: (hs.efficiency as number) || 0, detail: '' },
        Retention: { score: (hs.retention as number) || 0, detail: '' },
        Scale: { score: (hs.scale as number) || 0, detail: '' },
        Profitability: { score: (hs.profitability as number) || 0, detail: '' },
      },
    };
  }
  const hs2 = data.healthScore as Record<string, unknown> | undefined;
  if (hs2) {
    const inner = (hs2.health_score || hs2) as Record<string, unknown>;
    const dims = (hs2.dimensions || {}) as Record<string, Record<string, unknown>>;
    return {
      overall: (inner.overall as number) || 0,
      dimensions: Object.fromEntries(
        Object.entries(dims).map(([key, val]) => [
          key.charAt(0).toUpperCase() + key.slice(1),
          { score: (val.score as number) || 0, detail: String(val.detail || '') },
        ])
      ),
    };
  }
  return { overall: 0, dimensions: {} };
}

function scoreBarClass(score: number): string {
  if (score >= 70) return 'bg-success';
  if (score >= 40) return 'bg-warning';
  return 'bg-destructive';
}

function scoreTextClass(score: number): string {
  if (score >= 65) return 'text-success';
  if (score >= 40) return 'text-warning';
  return 'text-destructive';
}


/** A percentage cell that may arrive as a number (5.21), a numeric string
 *  ("5.21"), or prose ("~6% industry est."). Numbers get the % the reader
 *  expects; prose still goes through realValue's estimate filter. */
function pctCell(v: unknown): string | null {
  if (typeof v === 'number' && Number.isFinite(v)) return fmtPct(v);
  const s = realValue(v);
  if (s == null) return null;
  return /^-?\d+(\.\d+)?%?$/.test(s) ? fmtPct(parseFloat(s)) : s;
}

export default function ExecutiveSummary() {
  const data = useReportStore((s) => s.reportData);

  if (!data) return null;

  const raw = data as unknown as Record<string, unknown>;
  const { overall, dimensions } = getHealthScore(raw);

  // Vertical-aware labels: e-commerce reports read "Customer" instead of
  // "Subscriber". Backend sets raw._vertical; default to mobile-app.
  const L = labelsFor(raw._vertical as AuditVertical | undefined);

  // Key findings
  const keyFindings = useMemo(() => {
    const kf = raw.keyfindings || raw.keyFindings || raw.key_findings;
    if (Array.isArray(kf)) return kf;
    return [];
  }, [raw]);

  // Revenue data
  const revenueByYear = useMemo((): Array<{ year: string; revenue: number; phase?: string }> => {
    const rby = raw.revenueByYear;
    if (Array.isArray(rby)) return rby as Array<{ year: string; revenue: number; phase?: string }>;
    return [];
  }, [raw]);

  // Subscriber trend
  const subscriberTrend = useMemo((): Array<{ month: string; active: number; newPaid?: number }> => {
    const st = raw.subscriberTrend;
    if (Array.isArray(st)) return st as Array<{ month: string; active: number; newPaid?: number }>;
    return [];
  }, [raw]);

  // Channel benchmarks
  const channelBenchmarks = raw.channelBenchmarks;

  // Headline KPI grid (design prototype `.kpis`): one row of the numbers the
  // whole report hangs on. Cells with no value drop out rather than render
  // an em-dash card.
  const headlineKpis = useMemo(() => {
    const currency = raw._currency as string | undefined;
    const isEcommerce = raw._vertical === 'ecommerce' || raw._vertical === 'generic';
    // Compiler-written one-liners under each KPI ("-70% from Jan peak",
    // "3x benchmark") — a big number the reader can't judge is decoration.
    const notes = (raw.kpiNotes || {}) as Record<string, { text?: string; bad?: boolean }>;
    const note = (key: string): [string?, boolean?] => {
      const n = notes[key];
      return n?.text ? [n.text, !!n.bad] : [undefined, undefined];
    };
    const cells: Array<{ label: string; value: string; delta?: string; deltaType?: 'negative' | 'neutral' }> = [];
    const push = (label: string, value: string, delta?: string, bad?: boolean) => {
      if (value !== '--' && value !== '—') {
        cells.push({ label, value, delta, deltaType: bad ? 'negative' : 'neutral' });
      }
    };
    push(L.lifetimeRevenueLabel, fmt(raw.lifetimeRevenue as number, currency));
    push(
      'Total ad spend',
      raw._currency_mixed ? '—' : fmt(raw.totalAdSpend as number, currency),
      notes.totalAdSpend?.text ?? (raw.correctedROAS ? `${raw.correctedROAS} blended ROAS` : undefined),
      notes.totalAdSpend?.bad,
    );
    push(L.activeCount, fmtNum(raw.activeSubscribers as number), ...note('activeSubscribers'));
    push(L.arpuLabel, fmt((raw.arpu ?? raw.ltvPerSubscriber) as number, currency), ...note('ltv'));
    if (!isEcommerce) {
      const sh = (raw.subscriberHealth || {}) as Record<string, unknown>;
      const churn = pctCell(sh.churnRate) ?? (raw.churnRate != null ? fmtPct(raw.churnRate as number) : '--');
      push('Monthly churn', churn, ...note('churn'));
      // Prototype card: the primary funnel bottleneck belongs on the summary,
      // not two tabs away. Compiler-computed; falls back to the trial funnel.
      const tf = ((raw.funnel || {}) as Record<string, unknown>).trialFunnel as
        | Array<{ value?: number }>
        | undefined;
      const trialPct =
        (raw.trialStartRatePct as number | undefined) ??
        (Array.isArray(tf) && tf.length >= 2 && (tf[0]?.value ?? 0) > 0
          ? ((tf[1]?.value ?? 0) / tf[0]!.value!) * 100
          : undefined);
      if (trialPct != null) {
        push('Trial start rate', fmtPct(trialPct), ...note('trialStartRate'));
      }
    }
    push('Renewal rate', fmtPct(raw.renewalRate as number), ...note('renewalRate'));
    return cells.slice(0, 6);
  }, [raw, L]);

  return (
    <div>
      {/* Hero — gauge + key insights in one card (design prototype `.hero`) */}
      {overall > 0 && (
        <section className="mb-6.5">
          <Card>
            <CardContent className="flex flex-wrap gap-x-2 px-7 py-1.5">
              <div className="flex min-w-[210px] flex-[0_1_240px] flex-col items-center pt-1">
                <Gauge score={overall} />
                <div className={cn('-mt-1.5 text-[38px] font-bold leading-none tracking-[-0.03em] tabular-nums', scoreTextClass(overall))}>
                  {overall}
                </div>
                <div className="mt-2 text-[13px] text-muted-foreground">
                  Health Score · {overall}/100
                </div>
              </div>
              <div className="min-w-0 flex-[1_1_460px] pl-0 pt-1 md:pl-3">
                {keyFindings.length > 0 ? (
                  <KeyInsightsList findings={keyFindings} />
                ) : (
                  <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-2.5">
                    {Object.entries(dimensions).map(([name, dim]) => (
                      <KPICard key={name} value={`${dim.score}/100`} label={name} />
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </section>
      )}

      {/* Headline KPI grid */}
      {headlineKpis.length > 0 && (
        <section className="mb-6.5">
          <div className="grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-2.5">
            {headlineKpis.map((k) => (
              <KPICard key={k.label} {...k} />
            ))}
          </div>
        </section>
      )}

      {/* Health Dimensions */}
      {Object.keys(dimensions).length > 0 && (
        <Section title="Health dimensions" hint="Sub-scores feeding the overall health figure.">
          <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-2.5">
            {Object.entries(dimensions).map(([name, dim]) => (
              <Card size="sm" key={name}>
                <CardContent className="px-4.5 py-1">
                  <div className="text-sm font-semibold text-foreground">{name}</div>
                  <div className="mt-2.5 text-[28px] font-bold leading-none tracking-[-0.025em] tabular-nums text-foreground">
                    {dim.score}
                    <span className="text-[13px] font-medium tracking-normal text-muted-foreground/70">/100</span>
                  </div>
                  <div className="mb-3 mt-2 h-[5px] overflow-hidden rounded-full bg-muted">
                    <div
                      className={cn('h-full rounded-full', scoreBarClass(dim.score))}
                      style={{ width: `${dim.score}%` }}
                    />
                  </div>
                  {dim.detail && (
                    <p className="text-xs leading-relaxed text-muted-foreground">{String(dim.detail)}</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </Section>
      )}

      {/* Revenue journey. Silently hiding the card left the user asking "why
          did revenue disappear?" — explain the gate instead, same convention
          as the Scenario Analysis card. */}
      <Section title="Revenue journey" hint="Recognised revenue over the reporting window.">
        <Card>
          <CardContent className="px-5.5 py-1">
            {revenueByYear.length > 0 ? (
              <Bars
                data={revenueByYear.map((r) => ({ label: r.year, value: r.revenue }))}
                format={(v) => formatMoney(v, raw._currency as string)}
              />
            ) : (
              <div className="rounded-lg border border-dashed border-border p-6 text-center">
                <p className="text-sm font-medium text-foreground">Revenue data unavailable</p>
                <p className="mx-auto mt-1 max-w-md text-xs text-muted-foreground">
                  {raw._vertical === 'b2b'
                    ? "Connect a CRM export (HubSpot, Salesforce, or similar) to see revenue — lead-gen data alone can't measure won-deal revenue."
                    : 'Connect RevenueCat or Shopify to see revenue.'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </Section>

      {/* Subscriber / customer health */}
      {subscriberTrend.length > 0 && (() => {
        const sh = (raw.subscriberHealth || {}) as Record<string, unknown>;
        const vertical = raw._vertical as AuditVertical | undefined;
        const isEcommerce = vertical === 'ecommerce' || vertical === 'generic';
        const em = raw.ecommerceMetrics as {
          purchaseFrequency?: number | null;
          newRevenuePct?: number | null;
          returningRevenuePct?: number | null;
          checkoutConversionRate?: number | null;
          checkoutPageEngagementRate?: number | null;
        } | undefined;

        const cells: MiniCell[] = [
          { value: raw.activeSubscribers != null ? fmtNum(raw.activeSubscribers as number) : null, label: L.activeCount },
          { value: raw.renewalRate != null ? fmtPct(raw.renewalRate as number) : null, label: L.renewalMetric },
          // Lapsed-rate cards stay off for e-commerce — too noisy at low buyer
          // counts; subscription churn/retention stays for mobile_app.
          ...(isEcommerce ? [] : [
            { value: pctCell(sh.churnRate) ?? (raw.churnRate != null ? fmtPct(raw.churnRate as number) : null), label: L.repeatMetric },
            { value: pctCell(sh.retentionM1) ?? (raw.m1Retention != null ? fmtPct(raw.m1Retention as number) : null), label: L.retentionM1Label },
            { value: pctCell(sh.retentionM3), label: L.retentionM3Label },
            { value: pctCell(sh.retentionM12), label: L.retentionM12Label },
          ] as MiniCell[]),
        ];
        const emCells: MiniCell[] = em
          ? [
              { value: em.purchaseFrequency != null ? `${em.purchaseFrequency.toFixed(1)}x` : null, label: 'Purchase Frequency' },
              { value: em.newRevenuePct != null ? fmtPct(em.newRevenuePct) : null, label: 'New-Customer Revenue' },
              { value: em.returningRevenuePct != null ? fmtPct(em.returningRevenuePct) : null, label: 'Returning-Customer Revenue' },
              // Honest label switch: real GA4 begin_checkout rate when tracked,
              // else the page-engagement PROXY under its own "(est.)" label.
              em.checkoutConversionRate != null
                ? { value: fmtPct(em.checkoutConversionRate), label: 'Checkout Conversion Rate' }
                : em.checkoutPageEngagementRate != null
                  ? { value: fmtPct(em.checkoutPageEngagementRate), label: 'Checkout Page Engagement (est.)' }
                  : { value: null, label: 'Checkout Conversion Rate' },
            ]
          : [];
        const hasNewPaid = subscriberTrend.some((s) => s.newPaid != null);

        return (
          <Section title={L.audienceHealth} hint="Base size, retention instrumentation and the active-count curve.">
            <Card>
              <CardContent className="px-5.5 py-1">
                <MiniStrip cells={cells} />
                {emCells.length > 0 && <MiniStrip cells={emCells} />}
                <SmoothLine
                  data={subscriberTrend.map((s) => ({ label: s.month, value: s.active }))}
                  secondary={hasNewPaid ? subscriberTrend.map((s) => s.newPaid ?? 0) : undefined}
                />
                {hasNewPaid && (
                  <div className="mt-3 flex justify-end gap-4 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1.5">
                      <i className="h-[3px] w-3 rounded-sm bg-report-green" />
                      {L.activeCount}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <i className="h-[3px] w-3 rounded-sm bg-report-green/50" />
                      {L.newLegend}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          </Section>
        );
      })()}

      {/* Unit economics */}
      {!!channelBenchmarks && (
        <Section title="Unit economics" hint="Every channel below is measured against the same per-user value.">
          <Card>
            <CardContent className="px-5.5 py-1">
              <MiniStrip
                cells={[
                  // Headline is ARPU (always computable) over LTV (null on young cohorts).
                  // `??` binds tighter than `?:`, so the unparenthesized form
                  // tested the VALUE for truthiness — an ARPU of 0 (a real
                  // number on a free-heavy app) emptied the cell.
                  { value: (raw.arpu ?? raw.ltvPerSubscriber) != null ? fmt((raw.arpu ?? raw.ltvPerSubscriber) as number, raw._currency as string) : null, label: L.arpuLabel },
                  { value: raw._currency_mixed ? null : (raw.blendedCpa ?? raw.peakBlendedCPA) != null ? fmt((raw.blendedCpa ?? raw.peakBlendedCPA) as number, raw._currency as string) : null, label: 'Blended CPA' },
                  { value: raw._currency_mixed ? null : raw.blendedCpo != null ? fmt(raw.blendedCpo as number, raw._currency as string) : null, label: 'Blended CPO' },
                  { value: raw.peakLTVReturn ? `${raw.peakLTVReturn}x` : null, label: 'Best LTV Return' },
                  { value: raw.correctedROAS ? String(raw.correctedROAS) : null, label: 'Blended ROAS' },
                ].filter((c) => c.value != null)}
              />
              <ChannelBenchmarksTable channels={channelBenchmarks as any} vertical={raw._vertical as AuditVertical | undefined} />
            </CardContent>
          </Card>
        </Section>
      )}

      {/* Scenarios */}
      {(() => {
        const sc = raw.scenarios as Record<string, unknown> | undefined;
        if (!sc || typeof sc !== 'object') return null;
        // Gated when there's no real revenue source — projected CPA / LTV:CAC /
        // viability can't be modeled, so we show a note instead of benchmarks.
        const gated = !!sc.gated_no_revenue;
        if (!gated && !sc.insight) return null;
        return (
          <Section title="Scenario analysis" hint={gated ? undefined : String(sc.insight || '')}>
            <Card>
              <CardContent className={gated ? 'px-5.5 py-1' : 'p-0'}>
                {gated ? (
                  <div className="rounded-lg border border-dashed border-border p-6 text-center">
                    <p className="text-sm font-medium text-foreground">Scenario modeling unavailable</p>
                    <p className="mx-auto mt-1 max-w-md text-xs text-muted-foreground">
                      Connect RevenueCat to model growth scenarios — projected CPA, LTV:CAC,
                      and viability require real revenue and retention data.
                    </p>
                  </div>
                ) : (
                  Array.isArray(sc.scenarios) && (
                    <Table>
                      <TableHeader>
                        <TableRow className="hover:bg-transparent">
                          {['Scenario', 'Monthly spend', 'Est. CPA', 'LTV : CAC', 'Viable'].map((h, i) => (
                            <TableHead
                              key={h}
                              className={cn(
                                'bg-muted/40 px-4.5 font-mono text-[10px] font-semibold uppercase tracking-[0.07em] text-muted-foreground',
                                i === 4 && 'text-right',
                              )}
                            >
                              {h}
                            </TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(sc.scenarios as Array<Record<string, unknown>>).map((s, i) => (
                          <TableRow key={i}>
                            <TableCell className="px-4.5 font-semibold text-foreground">{String(s.scenario || '')}</TableCell>
                            <TableCell className="px-4.5 font-mono text-[13px] tabular-nums">{String(s.monthly_spend || '')}</TableCell>
                            <TableCell className="px-4.5 font-mono text-[13px] tabular-nums">{String(s.estimated_cpa || '')}</TableCell>
                            <TableCell className="px-4.5 font-mono text-[13px] tabular-nums">{String(s.ltv_cac_ratio || '')}</TableCell>
                            <TableCell className="px-4.5 text-right">
                              <StatusPill tone={s.viable ? 'ok' : 'crit'}>{s.viable ? 'Yes' : 'No'}</StatusPill>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )
                )}
              </CardContent>
            </Card>
          </Section>
        );
      })()}
    </div>
  );
}
